import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from './base-query.js'

/**
 * One access link as the list returns it to this caller.
 *
 * Declared here rather than imported from `@tunasong/access-links`: that is a backend package and
 * `lib/redux` does not, and should not, depend on one. The server projects per caller: `lastUsedAt`
 * is present only on the caller's own links.
 */
export interface AccessLinkView {
  linkId: string
  name: string
  createdAt: string
  createdBy: string
  /** When the link stops working; absent for calendar links */
  expiresAt?: string
  /** Set on a tombstone: the link was turned off because its creator left */
  revokedAt?: string
  revokedReason?: 'creator-left'
  /** Present only on the caller's own links */
  lastUsedAt?: string
}

/**
 * The client-visible result of minting or regenerating an access link.
 */
export interface CreatedAccessLink {
  /**
   * The link URL, token included.
   *
   * @important The create and regenerate responses are the ONLY ones in the whole access-link
   * surface that carry the token. The store keeps a hash, so it can never be retrieved again: a
   * caller that does not put this in front of the user has lost it, and the only remedy is another link.
   */
  url: string
  link: AccessLinkView
}

const linksTag = (entityId: string) => ({ type: 'AccessLinks' as const, id: entityId })

/**
 * Access-link management (GH #1853), mounted at the `links` mapping key (`access-links-stack.ts`).
 * These are ordinary JWT routes: a user manages links while logged in. Only the token routes (the
 * calendar feed, the rider venue sheet) consume the link authorizer.
 */
export const accessLinksApi = createApi({
  reducerPath: 'access-links-api',
  baseQuery,
  tagTypes: ['AccessLinks'],
  endpoints: builder => ({
    listAccessLinks: builder.query<AccessLinkView[], { entityId: string }>({
      query: ({ entityId }) => `links/${entityId}`,
      transformResponse: (response: { links: AccessLinkView[] }) => response.links,
      providesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
    createAccessLink: builder.mutation<CreatedAccessLink, { entityId: string; name: string; expiresAt?: string }>({
      query: ({ entityId, name, expiresAt }) => ({
        url: `links/${entityId}`,
        method: 'POST',
        body: expiresAt ? { name, expiresAt } : { name },
      }),
      invalidatesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
    regenerateAccessLink: builder.mutation<CreatedAccessLink, { entityId: string; linkId: string }>({
      query: ({ entityId, linkId }) => ({
        url: `links/${entityId}/${linkId}`,
        method: 'PATCH',
        body: { action: 'regenerate' },
      }),
      invalidatesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
    deleteAccessLink: builder.mutation<void, { entityId: string; linkId: string }>({
      query: ({ entityId, linkId }) => ({
        url: `links/${entityId}/${linkId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { entityId }) => [linksTag(entityId)],
    }),
  }),
})

export const {
  useCreateAccessLinkMutation,
  useListAccessLinksQuery,
  useRegenerateAccessLinkMutation,
  useDeleteAccessLinkMutation,
} = accessLinksApi
